interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface WakeLockSentinelLike extends EventTarget {
  release(): Promise<void>;
}

export interface PwaCallbacks {
  onInstallAvailability?(available: boolean): void;
  onInstalled?(): void;
  onUpdateReady?(): void;
}

const LEGACY_ACTIVE_RACE_LEASE_KEY = 'nightshift:active-race-tabs:v1';
const ACTIVE_RACE_LEASE_PREFIX = 'nightshift:active-race-tab:v2:';
const ACTIVE_RACE_LEASE_REFRESH_MS = 10_000;
const ACTIVE_RACE_LEASE_TTL_MS = 120_000;
const ACTIVE_RACE_LEASE_FUTURE_SKEW_MS = 30_000;
const WAKE_LOCK_RETRY_DELAYS_MS = [1_000, 5_000, 15_000, 30_000] as const;

interface FutureRaceLeaseObservation {
  expiresAt: number;
  observedAt: number;
}

export function racePhaseHoldsUpdateLease(phase: RacePhase): boolean {
  return phase === 'countdown' || phase === 'racing' || phase === 'finished';
}

export class PwaController {
  private installPrompt: InstallPromptEvent | null = null;
  private wakeLock: WakeLockSentinelLike | null = null;
  private wakeLockRequestPending = false;
  private wakeLockRetryTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
  private wakeLockRetryAttempt = 0;
  private raceActive = false;
  private wakeLockDesired = false;
  private started = false;
  private lifecycleGeneration = 0;
  private registration: ServiceWorkerRegistration | null = null;
  private installingWorker: ServiceWorker | null = null;
  private updateTimer: number | undefined;
  private updateReady = false;
  private activationRequested = false;
  private controllerReloaded = false;
  private controllerChangePending = false;
  private controllerEstablished = false;
  private protocolRecoveryStarted = false;
  private raceLeaseTimer: number | undefined;
  private raceLeaseExpiryTimer: number | undefined;
  private readonly futureRaceLeaseObservations = new Map<string, FutureRaceLeaseObservation>();
  private readonly tabId = `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  constructor(private readonly callbacks: PwaCallbacks = {}) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    const generation = ++this.lifecycleGeneration;
    window.addEventListener('beforeinstallprompt', this.onInstallPrompt);
    window.addEventListener('appinstalled', this.onInstalled);
    window.addEventListener('online', this.checkForUpdate);
    window.addEventListener('storage', this.onStorage);
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('pagehide', this.onPageHide);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      this.controllerEstablished = Boolean(navigator.serviceWorker.controller);
      navigator.serviceWorker.addEventListener('controllerchange', this.onControllerChange);
      void navigator.serviceWorker.register('/sw.js').then((registration) => {
        if (!this.isCurrentLifecycle(generation)) return;
        this.registration = registration;
        registration.addEventListener('updatefound', this.onRegistrationUpdateFound);
        this.trackInstallingWorker(registration.installing);
        if (registration.waiting) this.markUpdateReady();
        if (!this.isCurrentLifecycle(generation)) return;
        void registration.update().catch(() => undefined);
        this.updateTimer = window.setInterval(this.checkForUpdate, 30 * 60_000);
      }).catch(() => {
        // Install support is an enhancement; network racing remains available.
      });
    }
  }

  get installAvailable(): boolean {
    return this.installPrompt !== null;
  }

  async promptInstall(): Promise<boolean> {
    const prompt = this.installPrompt;
    if (!prompt) return false;
    // beforeinstallprompt events are single-use, including after dismissal.
    // Clear it before awaiting so a double tap cannot invoke prompt() twice.
    this.installPrompt = null;
    this.callbacks.onInstallAvailability?.(false);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      return choice.outcome === 'accepted';
    } catch {
      return false;
    }
  }

  setRaceActive(active: boolean): void {
    const wakeLockWasDesired = this.wakeLockDesired;
    this.raceActive = active;
    if (active) this.startRaceLease();
    else this.stopRaceLease();
    this.wakeLockDesired = active;
    if (active && !wakeLockWasDesired) this.wakeLockRetryAttempt = 0;
    if (active && !document.hidden) void this.acquireWakeLock();
    else {
      if (!active) this.wakeLockRetryAttempt = 0;
      this.clearWakeLockRetry();
      void this.releaseWakeLock();
    }
    if (!active && this.controllerChangePending) {
      this.reloadAfterControllerChange();
      return;
    }
    if (!active) this.activateWaitingUpdate();
  }

  /** Pauses only the screen wake lock; it does not make an active race update-safe. */
  setWakeLockActive(active: boolean): void {
    const wakeLockWasDesired = this.wakeLockDesired;
    this.wakeLockDesired = active;
    if (active && !wakeLockWasDesired) this.wakeLockRetryAttempt = 0;
    if (active && !document.hidden) void this.acquireWakeLock();
    else {
      if (!active) this.wakeLockRetryAttempt = 0;
      this.clearWakeLockRetry();
      void this.releaseWakeLock();
    }
  }

  async enterImmersiveMode(): Promise<boolean> {
    try {
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
      }
      const orientation = screen.orientation as ScreenOrientation & { lock?: (value: string) => Promise<void> };
      if (orientation.lock) await orientation.lock('landscape').catch(() => undefined);
      return Boolean(document.fullscreenElement);
    } catch {
      return false;
    }
  }

  /** Fetches/activates the newest shell after a wire-protocol mismatch. */
  async recoverProtocolMismatch(): Promise<void> {
    if (this.protocolRecoveryStarted) return;
    this.protocolRecoveryStarted = true;
    if ('serviceWorker' in navigator) {
      const registration = this.registration ?? await navigator.serviceWorker.getRegistration().catch(() => undefined);
      if (registration) {
        this.registration = registration;
        await registration.update().catch(() => undefined);
        const waiting = registration.waiting;
        if (waiting && !this.hasActiveRaceInAnyTab()) {
          this.activationRequested = true;
          waiting.postMessage({ type: 'SKIP_WAITING' });
          window.setTimeout(() => {
            if (!this.controllerReloaded) this.reloadWithCacheBust();
          }, 1_500);
          return;
        }
      }
    }
    this.reloadWithCacheBust();
  }

  destroy(): void {
    this.started = false;
    this.lifecycleGeneration += 1;
    this.stopRaceLease();
    this.raceActive = false;
    this.wakeLockDesired = false;
    window.removeEventListener('beforeinstallprompt', this.onInstallPrompt);
    window.removeEventListener('appinstalled', this.onInstalled);
    window.removeEventListener('online', this.checkForUpdate);
    window.removeEventListener('storage', this.onStorage);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('pagehide', this.onPageHide);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    navigator.serviceWorker?.removeEventListener('controllerchange', this.onControllerChange);
    this.detachServiceWorkerRegistration();
    if (this.updateTimer !== undefined) window.clearInterval(this.updateTimer);
    this.updateTimer = undefined;
    this.registration = null;
    this.updateReady = false;
    this.activationRequested = false;
    this.controllerChangePending = false;
    this.clearRaceLeaseExpiryTimer();
    this.futureRaceLeaseObservations.clear();
    this.clearWakeLockRetry();
    void this.releaseWakeLock();
  }

  private isCurrentLifecycle(generation: number): boolean {
    return this.started && generation === this.lifecycleGeneration;
  }

  private readonly onRegistrationUpdateFound = (): void => {
    if (!this.started) return;
    this.trackInstallingWorker(this.registration?.installing ?? null);
  };

  private readonly onInstallingWorkerStateChange = (): void => {
    const worker = this.installingWorker;
    if (!this.started || !worker) return;
    if (worker.state === 'installed' && navigator.serviceWorker.controller) this.markUpdateReady();
    if (worker.state === 'installed' || worker.state === 'activated' || worker.state === 'redundant') {
      worker.removeEventListener('statechange', this.onInstallingWorkerStateChange);
      if (this.installingWorker === worker) this.installingWorker = null;
    }
  };

  private trackInstallingWorker(worker: ServiceWorker | null): void {
    if (this.installingWorker === worker) return;
    this.installingWorker?.removeEventListener('statechange', this.onInstallingWorkerStateChange);
    this.installingWorker = worker;
    worker?.addEventListener('statechange', this.onInstallingWorkerStateChange);
    if (worker) this.onInstallingWorkerStateChange();
  }

  private detachServiceWorkerRegistration(): void {
    this.registration?.removeEventListener?.('updatefound', this.onRegistrationUpdateFound);
    this.installingWorker?.removeEventListener?.('statechange', this.onInstallingWorkerStateChange);
    this.installingWorker = null;
  }

  private async acquireWakeLock(): Promise<void> {
    if (
      this.wakeLock
      || this.wakeLockRequestPending
      || this.wakeLockRetryTimer !== undefined
      || !this.wakeLockDesired
      || document.hidden
    ) return;
    const wakeLockApi = (navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> };
    }).wakeLock;
    if (!wakeLockApi) return;
    this.wakeLockRequestPending = true;
    try {
      const lock = await wakeLockApi.request('screen');
      if (!this.wakeLockDesired || document.hidden) {
        await lock.release().catch(() => undefined);
        return;
      }
      this.wakeLock = lock;
      this.wakeLockRetryAttempt = 0;
      lock.addEventListener('release', () => {
        if (this.wakeLock !== lock) return;
        this.wakeLock = null;
        this.scheduleWakeLockRetry();
      }, { once: true });
    } catch {
      this.wakeLock = null;
    } finally {
      this.wakeLockRequestPending = false;
      if (!this.wakeLock) this.scheduleWakeLockRetry();
    }
  }

  private async releaseWakeLock(): Promise<void> {
    this.clearWakeLockRetry();
    const lock = this.wakeLock;
    this.wakeLock = null;
    if (lock) await lock.release().catch(() => undefined);
  }

  private scheduleWakeLockRetry(): void {
    if (
      this.wakeLockRetryTimer !== undefined
      || this.wakeLock
      || this.wakeLockRequestPending
      || !this.wakeLockDesired
      || document.hidden
    ) return;
    const delay = WAKE_LOCK_RETRY_DELAYS_MS[this.wakeLockRetryAttempt];
    if (delay === undefined) return;
    this.wakeLockRetryAttempt += 1;
    this.wakeLockRetryTimer = globalThis.setTimeout(() => {
      this.wakeLockRetryTimer = undefined;
      void this.acquireWakeLock();
    }, delay);
  }

  private clearWakeLockRetry(): void {
    if (this.wakeLockRetryTimer !== undefined) globalThis.clearTimeout(this.wakeLockRetryTimer);
    this.wakeLockRetryTimer = undefined;
  }

  private readonly onInstallPrompt = (event: Event): void => {
    event.preventDefault();
    this.installPrompt = event as InstallPromptEvent;
    this.callbacks.onInstallAvailability?.(true);
  };

  private readonly onInstalled = (): void => {
    this.installPrompt = null;
    this.callbacks.onInstallAvailability?.(false);
    this.callbacks.onInstalled?.();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) void this.releaseWakeLock();
    else {
      if (this.wakeLockDesired) {
        this.wakeLockRetryAttempt = 0;
        void this.acquireWakeLock();
      }
      this.checkForUpdate();
    }
  };

  private markUpdateReady(): void {
    this.updateReady = true;
    this.callbacks.onUpdateReady?.();
    if (!this.hasActiveRaceInAnyTab()) this.activateWaitingUpdate();
  }

  private activateWaitingUpdate(): void {
    if (!this.updateReady || this.activationRequested) return;
    if (this.hasActiveRaceInAnyTab()) {
      this.scheduleRaceLeaseExpiryCheck();
      return;
    }
    this.clearRaceLeaseExpiryTimer();
    const worker = this.registration?.waiting;
    if (!worker) return;
    this.activationRequested = true;
    worker.postMessage({ type: 'SKIP_WAITING' });
  }

  private readonly onControllerChange = (): void => {
    // clients.claim() also emits controllerchange when a first installation
    // adopts a previously uncontrolled page. The page already loaded this same
    // release, so reloading here can erase an invite/benchmark query or abort a
    // race while it is being constructed.
    if (!this.controllerEstablished && !this.activationRequested && !this.updateReady) {
      this.controllerEstablished = true;
      return;
    }
    this.controllerEstablished = true;
    // Every idle tab controlled by the old worker must reload, not only the tab
    // that requested activation. Cross-tab leases prevent this path while any
    // race is active.
    if (this.controllerReloaded) return;
    if (this.raceActive) {
      this.controllerChangePending = true;
      return;
    }
    this.reloadAfterControllerChange();
  };

  private reloadAfterControllerChange(): void {
    if (this.controllerReloaded) return;
    this.controllerChangePending = false;
    this.controllerReloaded = true;
    window.location.reload();
  }

  private readonly onStorage = (event: StorageEvent): void => {
    if (
      !this.updateReady
      || (
        event.key !== LEGACY_ACTIVE_RACE_LEASE_KEY
        && !event.key?.startsWith(ACTIVE_RACE_LEASE_PREFIX)
      )
    ) return;
    this.activateWaitingUpdate();
  };

  private readonly onPageShow = (): void => {
    if (this.raceActive) this.startRaceLease();
  };

  private readonly onPageHide = (event: PageTransitionEvent): void => {
    // A normally closed/navigated tab cannot refresh its lease. Remove it now;
    // BFCache pages retain it briefly and republish on pageshow.
    if (this.raceActive && !event.persisted) this.stopRaceLease();
    void this.releaseWakeLock();
  };

  private readonly checkForUpdate = (): void => {
    if (navigator.onLine === false) return;
    void this.registration?.update().catch(() => undefined);
  };

  private reloadWithCacheBust(): void {
    const url = new URL(window.location.href);
    url.searchParams.set('_nightshift_update', String(Date.now()));
    window.location.replace(url);
  }

  private startRaceLease(): void {
    this.writeRaceLease(true);
    if (this.raceLeaseTimer !== undefined || typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    this.raceLeaseTimer = window.setInterval(() => this.writeRaceLease(true), ACTIVE_RACE_LEASE_REFRESH_MS);
  }

  private stopRaceLease(): void {
    if (this.raceLeaseTimer !== undefined && typeof window !== 'undefined') {
      window.clearInterval(this.raceLeaseTimer);
    }
    this.raceLeaseTimer = undefined;
    this.writeRaceLease(false);
  }

  private scheduleRaceLeaseExpiryCheck(): void {
    this.clearRaceLeaseExpiryTimer();
    if (this.raceActive || !this.updateReady || typeof window === 'undefined') return;
    const now = Date.now();
    let nextExpiry = Number.POSITIVE_INFINITY;
    for (const [tabId, expiresAt] of Object.entries(this.readRaceLeases(now))) {
      if (tabId !== this.tabId && expiresAt > now) nextExpiry = Math.min(nextExpiry, expiresAt);
    }
    if (!Number.isFinite(nextExpiry)) return;
    this.raceLeaseExpiryTimer = window.setTimeout(() => {
      this.raceLeaseExpiryTimer = undefined;
      this.activateWaitingUpdate();
    }, Math.max(25, nextExpiry - now + 25));
  }

  private clearRaceLeaseExpiryTimer(): void {
    if (this.raceLeaseExpiryTimer !== undefined && typeof window !== 'undefined') {
      window.clearTimeout(this.raceLeaseExpiryTimer);
    }
    this.raceLeaseExpiryTimer = undefined;
  }

  private hasActiveRaceInAnyTab(): boolean {
    if (this.raceActive) return true;
    const now = Date.now();
    return Object.entries(this.readRaceLeases(now)).some(([tabId, expiresAt]) => (
      tabId !== this.tabId && expiresAt > now
    ));
  }

  private writeRaceLease(active: boolean): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const now = Date.now();
      const key = `${ACTIVE_RACE_LEASE_PREFIX}${this.tabId}`;
      if (active) localStorage.setItem(key, String(now + ACTIVE_RACE_LEASE_TTL_MS));
      else localStorage.removeItem(key);
      this.removeExpiredRaceLeases(now);
    } catch {
      // Storage can be unavailable in a private WebView. The current tab's
      // in-memory flag still prevents an update during its own race.
    }
  }

  private readRaceLeases(now = Date.now()): Record<string, number> {
    if (typeof localStorage === 'undefined') return {};
    const leases: Record<string, number> = {};
    try {
      const observedLeaseIds = new Set<string>();
      const invalidKeys: string[] = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!key?.startsWith(ACTIVE_RACE_LEASE_PREFIX)) continue;
        observedLeaseIds.add(key);
        const expiresAt = Number(localStorage.getItem(key));
        const effectiveExpiry = this.effectiveRaceLeaseExpiry(key, expiresAt, now);
        if (effectiveExpiry !== null) {
          leases[key.slice(ACTIVE_RACE_LEASE_PREFIX.length)] = effectiveExpiry;
        } else invalidKeys.push(key);
      }
      for (const key of invalidKeys) localStorage.removeItem(key);
      // Read the former shared-object key during a rolling update so a new tab
      // still respects an active race running under the previous shell.
      const legacy = JSON.parse(localStorage.getItem(LEGACY_ACTIVE_RACE_LEASE_KEY) ?? '{}') as unknown;
      if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
        const activeLegacy: Record<string, number> = {};
        for (const [tabId, expiresAt] of Object.entries(legacy as Record<string, unknown>)) {
          const leaseId = `${LEGACY_ACTIVE_RACE_LEASE_KEY}:${tabId}`;
          observedLeaseIds.add(leaseId);
          const numericExpiry = Number(expiresAt);
          const effectiveExpiry = this.effectiveRaceLeaseExpiry(leaseId, numericExpiry, now);
          if (effectiveExpiry === null) continue;
          activeLegacy[tabId] = numericExpiry;
          leases[`legacy:${tabId}`] = effectiveExpiry;
        }
        if (Object.keys(activeLegacy).length > 0) {
          localStorage.setItem(LEGACY_ACTIVE_RACE_LEASE_KEY, JSON.stringify(activeLegacy));
        } else {
          localStorage.removeItem(LEGACY_ACTIVE_RACE_LEASE_KEY);
        }
      }
      for (const leaseId of this.futureRaceLeaseObservations.keys()) {
        if (!observedLeaseIds.has(leaseId)) this.futureRaceLeaseObservations.delete(leaseId);
      }
      return leases;
    } catch {
      return leases;
    }
  }

  private removeExpiredRaceLeases(now: number): void {
    this.readRaceLeases(now);
  }

  private effectiveRaceLeaseExpiry(leaseId: string, expiresAt: number, now: number): number | null {
    if (!Number.isFinite(expiresAt) || expiresAt <= now) {
      this.futureRaceLeaseObservations.delete(leaseId);
      return null;
    }
    if (expiresAt <= now + ACTIVE_RACE_LEASE_TTL_MS + ACTIVE_RACE_LEASE_FUTURE_SKEW_MS) {
      this.futureRaceLeaseObservations.delete(leaseId);
      return expiresAt;
    }

    const monotonicNow = globalThis.performance.now();
    let observation = this.futureRaceLeaseObservations.get(leaseId);
    if (
      !observation
      || observation.expiresAt !== expiresAt
      || monotonicNow < observation.observedAt
    ) {
      observation = { expiresAt, observedAt: monotonicNow };
      this.futureRaceLeaseObservations.set(leaseId, observation);
    }
    const remainingGrace = ACTIVE_RACE_LEASE_TTL_MS - (monotonicNow - observation.observedAt);
    if (remainingGrace <= 0) {
      this.futureRaceLeaseObservations.delete(leaseId);
      return null;
    }
    return now + remainingGrace;
  }
}
import type { RacePhase } from '../shared/types.js';
